/** Tag category → General tab.
 *
 * Basics (code/name/keyfield) + flags + the schema section. The schema section
 * ENCOURAGES a reusable `schema_ref` (the primary picker) and DISCOURAGES inline
 * schemas (tucked behind an "Advanced" disclosure, with an override warning and a
 * one-click "promote inline → reusable schema"). Access groups are shown
 * read-only (managed via RBAC).
 */
import { zodResolver } from '@hookform/resolvers/zod';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';

import { labelWithHelp, type FieldHelpEntry } from '@/components/FieldHelp';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import * as tagCategoriesApi from '@/api/tagCategories';
import type { TagCategoryDetail, TagCategorySchemaRef } from '@/api/tagCategories';
import * as tagSchemasApi from '@/api/tagSchemas';
import { EditorSaveBar } from '@/components/EditorSaveBar/EditorSaveBar';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { usePermissions } from '@/hooks/usePermissions';

import { TAG_CATEGORY_HELP } from '../fieldHelp';
import { categoryAccessGroupActions } from '../tagLinks';
import { SchemaEditor } from '../schema/SchemaEditor';
import { emptySchema } from '../schema/schemaEditorOps';
import type { TagSchema } from '../schema/vocabulary';
import { TagSchemaPicker } from '../TagSchemaPicker';
import { PromoteInlineSchemaDialog } from './PromoteInlineSchemaDialog';

const FLAGS = ['state_category', 'is_multitag', 'external_identity', 'accounting', 'billing'] as const;
const FLAG_LABELS: Record<(typeof FLAGS)[number], string> = {
  state_category: 'State category',
  is_multitag: 'Multitag',
  external_identity: 'Externally identified',
  accounting: 'Accounting',
  billing: 'Billing',
};
/** Flags worth a help popover rather than a bare label. */
const FLAG_HELP: Partial<Record<(typeof FLAGS)[number], FieldHelpEntry>> = {
  external_identity: TAG_CATEGORY_HELP.external_identity,
};

const Schema = z
  .object({
    code: z.string().trim().min(1, 'Code is required').max(64),
    name: z.string().trim().min(1, 'Name is required').max(1024),
    keyfield: z.string().trim().max(64),
    state_category: z.boolean(),
    is_multitag: z.boolean(),
    external_identity: z.boolean(),
    accounting: z.boolean(),
    billing: z.boolean(),
  })
  // Mirrors Group_Tour_Tag_Category.clean(): a multitag category keys its tag
  // context by `keyfield`, so once external identity lets two tags share a name
  // a 'name' key collides and one tag is dropped with no error. The backend
  // refuses this too (400); catching it here means the operator is told which
  // field to change before the round-trip.
  .superRefine((v, ctx) => {
    if (v.external_identity && v.is_multitag && (v.keyfield.trim() || 'name') === 'name') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['external_identity'],
        message:
          "A multitag category cannot use external identity while its key field is 'name': "
          + "two tags sharing a name would collide and one would be dropped. Set the key field to 'custom_id' first.",
      });
    }
  });
type FormValues = z.infer<typeof Schema>;

const toDefaults = (c: TagCategoryDetail): FormValues => ({
  code: c.code,
  name: c.name,
  keyfield: c.keyfield || 'name',
  state_category: c.state_category,
  is_multitag: c.is_multitag,
  external_identity: c.external_identity,
  accounting: c.accounting,
  billing: c.billing,
});

export function TagCategoryGeneralTab({ category, onSaved }: { category: TagCategoryDetail; onSaved: () => void }) {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_tag_categories_manage');
  const snackbar = useSnackbar();

  const { control, handleSubmit, reset, formState } = useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: toDefaults(category),
  });

  const [schemaRef, setSchemaRef] = useState<TagCategorySchemaRef | null>(category.schema_ref);
  const [inline, setInline] = useState<TagSchema | null>(category.schema);
  const [promoteOpen, setPromoteOpen] = useState(false);

  const resetToRecord = () => {
    reset(toDefaults(category));
    setSchemaRef(category.schema_ref);
    setInline(category.schema);
  };
  useEffect(() => {
    resetToRecord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const refChanged = (schemaRef?.id ?? null) !== (category.schema_ref?.id ?? null);
  const inlineChanged = useMemo(
    () => JSON.stringify(inline ?? null) !== JSON.stringify(category.schema ?? null),
    [inline, category.schema],
  );
  const dirty = formState.isDirty || refChanged || inlineChanged;

  const patch = useMutation({
    mutationFn: (body: tagCategoriesApi.TagCategoryWriteInput) => tagCategoriesApi.update(category.id, body),
    onSuccess: () => {
      snackbar.show('Saved.', 'success');
      onSaved();
    },
    onError: (e) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error'),
  });

  const promote = useMutation({
    mutationFn: async (v: { name: string; codename: string }) => {
      const created = await tagSchemasApi.create({ name: v.name, codename: v.codename, schema: inline });
      return tagCategoriesApi.update(category.id, { schema_ref: created.id, schema: null });
    },
    onSuccess: () => {
      snackbar.show('Inline schema promoted to a reusable schema.', 'success');
      setPromoteOpen(false);
      onSaved();
    },
    onError: (e) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Promote failed.', 'error'),
  });

  const submit = handleSubmit((v) =>
    patch.mutate({
      code: v.code.trim(),
      name: v.name.trim(),
      keyfield: v.keyfield.trim() || 'name',
      state_category: v.state_category,
      is_multitag: v.is_multitag,
      external_identity: v.external_identity,
      accounting: v.accounting,
      billing: v.billing,
      schema_ref: schemaRef?.id ?? null,
      schema: inline,
    }),
  );

  const err = formState.errors;

  return (
    <form onSubmit={submit}>
      {/* Basics */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Basics</Typography>
      <Stack spacing={2} sx={{ maxWidth: 640, mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Tenant: {category.tenant ? `${category.tenant.name} (${category.tenant.code})` : 'System default'}
        </Typography>
        <Controller control={control} name="code" render={({ field }) => (
          <TextField {...field} size="small" fullWidth disabled={!canManage} label="Code"
            error={!!err.code} helperText={err.code?.message} />
        )} />
        <Controller control={control} name="name" render={({ field }) => (
          <TextField {...field} size="small" fullWidth disabled={!canManage} label="Name"
            error={!!err.name} helperText={err.name?.message} />
        )} />
        <Controller control={control} name="keyfield" render={({ field }) => (
          <TextField {...field} size="small" sx={{ width: 260 }} disabled={!canManage} label="Key field"
            error={!!err.keyfield} helperText={err.keyfield?.message ?? 'Tag field used as key for multitag categories.'} />
        )} />
      </Stack>

      {/* Flags */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Flags</Typography>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        {FLAGS.map((flag) => {
          const help = FLAG_HELP[flag];
          return (
            <Controller key={flag} control={control} name={flag} render={({ field }) => (
              <FormControlLabel
                control={<Switch checked={field.value} disabled={!canManage} onChange={(e) => field.onChange(e.target.checked)} />}
                label={help ? labelWithHelp(FLAG_LABELS[flag], help) : FLAG_LABELS[flag]}
              />
            )} />
          );
        })}
      </Stack>
      {err.external_identity && (
        <Alert severity="error" sx={{ maxWidth: 720, mb: 3 }}>{err.external_identity.message}</Alert>
      )}
      <Box sx={{ mb: 3 }} />

      {/* Schema */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Schema</Typography>
      <Stack spacing={1.5} sx={{ maxWidth: 720, mb: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          Prefer a reusable schema so it can be shared and versioned across categories.
        </Typography>
        {canManage ? (
          <TagSchemaPicker value={schemaRef} onChange={setSchemaRef} />
        ) : (
          <Typography variant="body2">
            {schemaRef ? `${schemaRef.name} (${schemaRef.codename})` : 'No reusable schema'}
          </Typography>
        )}
        {schemaRef && (
          <RouterLink to={`/tag-schemas/${schemaRef.id}/schema`} style={{ color: '#0a1a3f', fontSize: 13 }}>
            View / edit this schema →
          </RouterLink>
        )}

        {inline !== null && schemaRef !== null && (
          <Alert severity="warning">
            This category has BOTH an inline schema and a reusable schema. The inline schema takes precedence
            (matches the backend). Remove the inline schema to use the reusable one.
          </Alert>
        )}
      </Stack>
      {/* Full width, not the 720px above: the schema editor's two columns need the room (CP-7988). */}
      <Accordion disableGutters sx={{ mb: 3, '&:before': { display: 'none' } }} defaultExpanded={inline !== null}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">Advanced: inline schema (not reusable — discouraged)</Typography>
            {inline !== null && <Chip size="small" color="warning" variant="outlined" label="inline set" />}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          {inline === null ? (
            <Button size="small" variant="outlined" disabled={!canManage} onClick={() => setInline(emptySchema())}>
              Define an inline schema
            </Button>
          ) : (
            <Box>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Button size="small" variant="outlined" disabled={!canManage} onClick={() => setPromoteOpen(true)}>
                  Promote to reusable schema
                </Button>
                <Button size="small" color="error" disabled={!canManage} onClick={() => setInline(null)}>
                  Remove inline schema
                </Button>
              </Stack>
              <SchemaEditor value={inline} onChange={(next) => setInline(next)} readOnly={!canManage} />
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Access groups (read-only) */}
      {category.agroups.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Access groups</Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {category.agroups.map((g) => (
              <ValueActions
                key={g.id}
                chip
                actions={categoryAccessGroupActions(g, { tenantId: category.tenant?.id })}
              >
                <Chip size="small" variant="outlined" label={g.name ?? `#${g.id}`} />
              </ValueActions>
            ))}
          </Stack>
        </Box>
      )}

      <EditorSaveBar
        dirty={dirty} saving={patch.isPending} canManage={canManage}
        onSave={submit} onDiscard={resetToRecord}
      />

      <PromoteInlineSchemaDialog
        open={promoteOpen}
        defaultName={`${category.name} schema`}
        saving={promote.isPending}
        onCancel={() => setPromoteOpen(false)}
        onConfirm={(v) => promote.mutate(v)}
      />
    </form>
  );
}
